/**
 * Dummy Placement Algorithm - Simplified Greedy First-Fit with Segmentation
 * For testing temporal parking without the full spatial solver
 */

import { dwellMask, orInto } from "./bitmap";
import {
  countTrappedAircraft,
  countTrappedAircraftFast,
  findTrappedAircraft,
} from "./egress";
import { precomputeAllEgressMasks } from "./egressMasks";
import { createAircraftFootprint, Point } from "./geometryUtils";
import { findSpotChainWithLocks } from "./lockSegmentation";
import { applySpotChain, findSpotChain } from "./segmentation";
import { getSortingStrategy, SortingStrategy } from "./sortingStrategies";
import {
  getAvailableSpotsWithBreakdown,
  hasGeometryCollision,
  isDurationValid,
  isSizeCompatible,
  isSpotEnabled,
  isTimeAvailable,
} from "./spotFiltering";
import {
  isSpotInStagingZone,
  shouldUseStaging,
} from "./stagingArea";
import { findStagingChain } from "./stagingSegmentation";
import {
  ActionableSuggestion,
  DurationAnalysis,
  FilterBreakdown,
  LogEntry,
  MINUTES_PER_BUCKET,
  NearMissSpot,
  ParkingRequest,
  Placement,
  SizeAnalysis,
  SizeCategory,
  Spot,
  STAGING_OVERFLOW_PENALTY,
  SuggestionDetails,
  TimeAnalysis,
  Zone,
} from "./types";
import { getAircraftDisplayName } from "./utils/aircraftUtils";
import { createTaggedLogger } from "./utils/logger";
import {
  calculateSpotWaste,
  calculateStructuralComplexity,
} from "./wasteScoring";

// Global decision log (reset on each algorithm run)
let decisionLog: LogEntry[] = [];

// Tagged logger for staging-related operations
const stagingLogger = createTaggedLogger('STAGING');

export function getDecisionLog(): LogEntry[] {
  return decisionLog;
}

function logEntry(entry: LogEntry): void {
  decisionLog.push(entry);
}

/**
 * Generate actionable suggestions based on filter breakdown
 * Identifies the primary blocker and provides specific recommendations
 *
 * @param breakdown - Filter breakdown showing elimination counts
 * @param aircraft - Aircraft that couldn't be placed
 * @returns Suggestion string with actionable advice
 */
function generateFilterSuggestion(
  breakdown: FilterBreakdown,
  aircraft: ParkingRequest
): string {
  // Find the filter that eliminated the most spots
  const filters = [
    {
      name: "disabled",
      count: breakdown.disabled,
      suggestion: "Enable more spots or zones that are currently disabled",
    },
    {
      name: "sizeIncompatible",
      count: breakdown.sizeIncompatible,
      suggestion: `Add more spots that allow ${aircraft.sizeCategory} aircraft, or use smaller aircraft`,
    },
    {
      name: "timeConflict",
      count: breakdown.timeConflict,
      suggestion:
        "Reduce scheduling conflicts by adjusting arrival/departure times or adding more spots",
    },
    {
      name: "durationExceeded",
      count: breakdown.durationExceeded,
      suggestion:
        "Increase max_dwell_minutes limits on overflow spots or shorten aircraft stay",
    },
    {
      name: "geometryCollision",
      count: breakdown.geometryCollision,
      suggestion: "Move blocking aircraft to free up gap spots",
    },
  ];

  // Sort by count descending to find primary blocker
  filters.sort((a, b) => b.count - a.count);

  const primaryBlocker = filters[0];
  if (primaryBlocker.count === 0) {
    return "No spots configured for this ramp";
  }

  return primaryBlocker.suggestion;
}

/**
 * Identify "near miss" spots that passed most filters but failed on exactly one
 * Inspired by Gurobi's IIS (Irreducible Inconsistent Subsystem) concept
 * These represent the easiest opportunities for placement
 *
 * @param spots - All spots to analyze
 * @param aircraft - Aircraft that couldn't be placed
 * @param dwellMask - Time mask for aircraft dwell
 * @param placement - Current placement state
 * @param placedAircraftPolygons - Polygon map for geometry checking
 * @param shapes - Shapes for geometry checking
 * @param zones - All zones on the ramp
 * @returns List of near-miss spots with failure details
 */
function findNearMissSpots(
  spots: Spot[],
  aircraft: ParkingRequest,
  dwellMask: BigUint64Array,
  placement: Placement,
  placedAircraftPolygons?: Map<string, Point[]>,
  // shapes parameter REMOVED - use spot.shape instead
  zones: Zone[] = []
): NearMissSpot[] {
  const nearMisses: NearMissSpot[] = [];

  for (const spot of spots) {
    const passedFilters: string[] = [];
    let failedFilter:
      | ("disabled" | "size" | "time" | "duration" | "geometry")
      | null = null;
    let specificIssue = "";
    let easyFix: string | undefined;

    // Check each filter in order
    const enabled = isSpotEnabled(spot, zones);
    if (enabled) {
      passedFilters.push("enabled");
    } else {
      if (failedFilter === null) {
        failedFilter = "disabled";
        const zone = zones.find((z) => z.id === spot.zoneId);
        if (spot.disabled) {
          specificIssue = `Spot ${spot.id} is disabled`;
          easyFix = `Enable spot ${spot.id}`;
        } else if (zone?.disabled) {
          specificIssue = `Zone ${zone.name} is disabled`;
          easyFix = `Enable zone ${zone.name}`;
        }
      }
    }

    const sizeOk = isSizeCompatible(spot, aircraft);
    if (sizeOk) {
      passedFilters.push("size");
    } else {
      if (failedFilter === null) {
        failedFilter = "size";
        specificIssue = `Spot allows ${spot.allowedSizes.join(
          ", "
        )} but aircraft is ${aircraft.sizeCategory}`;
        easyFix = `Change spot ${spot.id} to allow ${aircraft.sizeCategory} aircraft`;
      }
    }

    const timeOk = isTimeAvailable(spot, dwellMask);
    if (timeOk) {
      passedFilters.push("time");
    } else {
      if (failedFilter === null) {
        failedFilter = "time";
        const formatBucket = (bucket: number) => {
          const hours = Math.floor(bucket / 4);
          const mins = (bucket % 4) * 15;
          return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
            2,
            "0"
          )}`;
        };
        specificIssue = `Spot occupied during ${formatBucket(
          aircraft.arrivalBucket
        )}-${formatBucket(aircraft.departureBucket)}`;
        easyFix = `Shift arrival/departure to avoid conflict`;
      }
    }

    const durationOk = isDurationValid(spot, aircraft);
    if (durationOk) {
      passedFilters.push("duration");
    } else {
      if (failedFilter === null) {
        failedFilter = "duration";
        const dwellMinutes =
          (aircraft.departureBucket - aircraft.arrivalBucket) * 15;
        specificIssue = `Needs ${dwellMinutes} min, limit: ${spot.max_dwell_minutes} min`;
        easyFix = `Increase max_dwell_minutes to ${dwellMinutes} minutes`;
      }
    }

    const geometryOk = !hasGeometryCollision(
      spot,
      aircraft,
      placement,
      placedAircraftPolygons
      // shapes parameter removed
    );
    if (geometryOk) {
      passedFilters.push("geometry");
    } else {
      if (failedFilter === null) {
        failedFilter = "geometry";
        specificIssue = `Gap spot blocked by aircraft geometry`;
        easyFix = `Reposition blocking aircraft`;
      }
    }

    // Only include spots that passed at least 3 filters (failed on 1 or 2)
    // These are the "closest" to working
    if (passedFilters.length >= 3 && failedFilter !== null) {
      nearMisses.push({
        spotId: spot.id,
        failedFilter,
        passedFilters,
        specificIssue,
        easyFix,
      });
    }
  }

  // Sort by number of passed filters (descending) - closest to working first
  nearMisses.sort((a, b) => b.passedFilters.length - a.passedFilters.length);

  // Return top 3 near-misses
  return nearMisses.slice(0, 3);
}

/**
 * Generate enhanced actionable suggestions with numeric analysis
 * Inspired by best practices from Rust compiler, Kubernetes, PostgreSQL, and Gurobi
 *
 * @param breakdown - Filter breakdown showing elimination counts
 * @param aircraft - Aircraft that couldn't be placed
 * @param spots - All spots on the ramp
 * @param zones - All zones on the ramp
 * @param dwellMask - Time mask for near-miss analysis
 * @param placement - Current placement for near-miss analysis
 * @param placedAircraftPolygons - Polygons for near-miss analysis
 * @param shapes - Shapes for near-miss analysis
 * @returns Detailed suggestion with analysis and actionable recommendations
 */
function generateEnhancedSuggestions(
  breakdown: FilterBreakdown,
  aircraft: ParkingRequest,
  spots: Spot[],
  zones: Zone[] = [],
  dwellMask?: BigUint64Array,
  placement?: Placement,
  placedAircraftPolygons?: Map<string, Point[]>
  // shapes parameter REMOVED - use spot.shape instead
): SuggestionDetails {
  const suggestions: ActionableSuggestion[] = [];

  // Determine primary blocker
  const filters = [
    { name: "disabled" as const, count: breakdown.disabled },
    { name: "size" as const, count: breakdown.sizeIncompatible },
    { name: "time" as const, count: breakdown.timeConflict },
    { name: "duration" as const, count: breakdown.durationExceeded },
    { name: "geometry" as const, count: breakdown.geometryCollision },
  ];
  filters.sort((a, b) => b.count - a.count);
  const primaryBlocker = filters[0].count > 0 ? filters[0].name : "none";

  // Size Analysis (Phase 1: Numeric Specificity)
  let sizeAnalysis: SizeAnalysis | undefined;
  if (breakdown.sizeIncompatible > 0) {
    const spotsBySize: Record<SizeCategory, number> = {
      "very-small": 0,
      small: 0,
      midsize: 0,
      "super-midsize": 0,
      large: 0,
      enormous: 0,
    };

    // Count spots by their maximum allowed size
    spots.forEach((spot) => {
      const maxSize = spot.allowedSizes[spot.allowedSizes.length - 1];
      if (maxSize) {
        spotsBySize[maxSize]++;
      }
    });

    // Count current spots that can fit this aircraft
    const currentAvailable = spots.filter((spot) =>
      spot.allowedSizes.includes(aircraft.sizeCategory)
    ).length;

    sizeAnalysis = {
      aircraftSize: aircraft.sizeCategory,
      aircraftWingspan: aircraft.aircraft?.wingspan,
      aircraftLength: aircraft.aircraft?.length,
      spotsBySize,
      minimumNeeded: 1,
      currentAvailable,
    };

    // Generate size-based suggestions (infrastructure/configuration only)
    const sizePriority = primaryBlocker === "size" ? 1 : 2;

    // Suggest adding spots of the right size
    suggestions.push({
      priority: sizePriority,
      category: "size",
      title: `Add ${aircraft.sizeCategory} spots to ramp`,
      description: `Only ${currentAvailable} of ${breakdown.total} spots accommodate ${aircraft.sizeCategory} aircraft`,
      impact: undefined, // Can't predict how many you'll add
      specificAction: `Add spots that allow ${aircraft.sizeCategory} aircraft`,
    });

    // Suggest reconfiguring existing spots to allow this size
    suggestions.push({
      priority: sizePriority + 1,
      category: "size",
      title: `Reconfigure spots to allow ${aircraft.sizeCategory}`,
      description: `${breakdown.sizeIncompatible} spots currently exclude ${aircraft.sizeCategory} aircraft`,
      impact: `Up to +${breakdown.sizeIncompatible} spots`,
      specificAction: `Update spot size policies to include ${aircraft.sizeCategory}`,
    });
  }

  // Time Analysis (Phase 2: Temporal Analysis)
  let timeAnalysis: TimeAnalysis | undefined;
  if (breakdown.timeConflict > 0) {
    const dwellDurationBuckets =
      aircraft.departureBucket - aircraft.arrivalBucket;
    const dwellDurationMinutes = dwellDurationBuckets * 15;

    // Analyze when congestion peaks by checking occupancy across all size-compatible spots
    // This helps identify the specific time window causing conflicts
    let peakCongestionStart: number | undefined;
    let peakCongestionEnd: number | undefined;
    let peakOccupiedSpots: number | undefined;

    const sizeCompatibleSpots = spots.filter((spot) =>
      spot.allowedSizes.includes(aircraft.sizeCategory)
    );

    if (sizeCompatibleSpots.length > 0) {
      // Count how many spots are occupied at each bucket during aircraft's dwell
      const occupancyByBucket: number[] = [];
      for (
        let bucket = aircraft.arrivalBucket;
        bucket < aircraft.departureBucket;
        bucket++
      ) {
        let occupiedCount = 0;
        for (const spot of sizeCompatibleSpots) {
          // Check if this bucket is set in the spot's occupancy bitmap
          const wordIndex = Math.floor(bucket / 64);
          const bitIndex = bucket % 64;
          if (spot.occupancy[wordIndex] & (BigInt(1) << BigInt(bitIndex))) {
            occupiedCount++;
          }
        }
        occupancyByBucket.push(occupiedCount);
      }

      // Find the peak congestion period (consecutive buckets with highest occupancy)
      peakOccupiedSpots = Math.max(...occupancyByBucket);
      if (peakOccupiedSpots > 0) {
        // Find first occurrence of peak
        const peakIndex = occupancyByBucket.indexOf(peakOccupiedSpots);
        peakCongestionStart = aircraft.arrivalBucket + peakIndex;

        // Find end of peak period (last consecutive bucket with peak occupancy)
        let endIndex = peakIndex;
        while (
          endIndex < occupancyByBucket.length &&
          occupancyByBucket[endIndex] >= peakOccupiedSpots * 0.8 // Within 80% of peak
        ) {
          endIndex++;
        }
        peakCongestionEnd = aircraft.arrivalBucket + endIndex;
      }
    }

    const conflictWindowBuckets =
      peakCongestionStart !== undefined && peakCongestionEnd !== undefined
        ? peakCongestionEnd - peakCongestionStart
        : undefined;

    timeAnalysis = {
      aircraftArrivalBucket: aircraft.arrivalBucket,
      aircraftDepartureBucket: aircraft.departureBucket,
      dwellDurationBuckets,
      dwellDurationMinutes,
      peakCongestionStart,
      peakCongestionEnd,
      peakOccupiedSpots,
      conflictWindowBuckets,
    };

    // Generate time-based suggestions (prioritize infrastructure over coordination)
    const timePriority = primaryBlocker === "time" ? 1 : 3;

    // Suggestion 1: Add capacity (infrastructure - direct control)
    suggestions.push({
      priority: timePriority,
      category: "time",
      title: "Add more spots to handle peak demand",
      description: `${breakdown.timeConflict} of ${breakdown.total} spots have scheduling conflicts`,
      impact: undefined, // Can't predict capacity additions
      specificAction: `Add spots to accommodate concurrent aircraft during peak periods`,
    });

    // Suggestion 2: Coordinate timing (requires customer agreement - lower priority)
    if (
      peakCongestionStart !== undefined &&
      peakCongestionEnd !== undefined &&
      conflictWindowBuckets !== undefined
    ) {
      const formatBucket = (bucket: number) => {
        const hours = Math.floor(bucket / 4);
        const mins = (bucket % 4) * 15;
        return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
          2,
          "0"
        )}`;
      };

      const delayMinutes = (peakCongestionEnd - aircraft.arrivalBucket) * 15;

      suggestions.push({
        priority: timePriority + 1,
        category: "time",
        title: `Coordinate ${delayMinutes}-min delay (if customer agrees)`,
        description: `Peak congestion ${formatBucket(
          peakCongestionStart
        )}-${formatBucket(peakCongestionEnd)} blocks ${peakOccupiedSpots} of ${
          sizeCompatibleSpots.length
        } spots`,
        impact: "May reduce conflicts",
        specificAction: `Coordinate with customer to shift arrival from ${formatBucket(
          aircraft.arrivalBucket
        )} to ${formatBucket(peakCongestionEnd)}`,
      });
    } else {
      // Fallback if peak analysis fails
      suggestions.push({
        priority: timePriority + 1,
        category: "time",
        title: "Coordinate schedule adjustment (if customer agrees)",
        description: `${breakdown.timeConflict} spots have scheduling conflicts`,
        impact: "May reduce conflicts",
        specificAction: "Work with customer to adjust arrival/departure times",
      });
    }
  }

  // Duration Analysis (Phase 1)
  let durationAnalysis: DurationAnalysis | undefined;
  if (breakdown.durationExceeded > 0) {
    const requiredDwellMinutes =
      (aircraft.departureBucket - aircraft.arrivalBucket) * 15;
    const spotsWithLimits = spots.filter(
      (s) => s.max_dwell_minutes !== undefined
    ).length;

    const limitsValues = spots
      .filter((s) => s.max_dwell_minutes !== undefined)
      .map((s) => s.max_dwell_minutes!);
    const mostRestrictiveLimit =
      limitsValues.length > 0 ? Math.min(...limitsValues) : undefined;
    const excessDuration = mostRestrictiveLimit
      ? requiredDwellMinutes - mostRestrictiveLimit
      : undefined;

    durationAnalysis = {
      requiredDwellMinutes,
      spotsWithLimits,
      mostRestrictiveLimit,
      excessDuration,
    };

    // Generate duration-based suggestions (configuration control)
    const durationPriority = primaryBlocker === "duration" ? 1 : 4;

    if (mostRestrictiveLimit !== undefined && excessDuration !== undefined) {
      // Suggestion: Extend limits (direct control)
      suggestions.push({
        priority: durationPriority,
        category: "duration",
        title: `Extend max dwell limits to ${requiredDwellMinutes} min`,
        description: `Aircraft needs ${requiredDwellMinutes} min but overflow spots limited to ${mostRestrictiveLimit} min`,
        impact: `+${breakdown.durationExceeded} spots available`,
        specificAction: `Increase max_dwell_minutes from ${mostRestrictiveLimit} to ${requiredDwellMinutes} minutes`,
      });
    }
  }

  // Disabled spots analysis (configuration - high priority, immediate impact)
  if (breakdown.disabled > 0) {
    // Highest priority for disabled spots since it's easy and immediate
    const disabledPriority = primaryBlocker === "disabled" ? 1 : 2;

    suggestions.push({
      priority: disabledPriority,
      category: "disabled",
      title: "Enable disabled spots/zones",
      description: `${breakdown.disabled} spots currently disabled (maintenance/construction)`,
      impact: `+${breakdown.disabled} spots`,
      specificAction: "Re-enable spots/zones in ramp configuration",
    });
  }

  // Geometry collision analysis (operational - requires aircraft movement)
  if (breakdown.geometryCollision > 0) {
    const geometryPriority = primaryBlocker === "geometry" ? 1 : 5;

    suggestions.push({
      priority: geometryPriority,
      category: "geometry",
      title: "Reposition aircraft to free gap spots",
      description: `${breakdown.geometryCollision} gap spots blocked by aircraft geometry`,
      impact: `+${breakdown.geometryCollision} spots`,
      specificAction:
        "Move aircraft in required spots to eliminate geometry collision",
    });
  }

  // Sort suggestions by priority
  suggestions.sort((a, b) => a.priority - b.priority);

  // Phase 4: Find near-miss spots (spots that almost worked)
  let nearMisses: NearMissSpot[] | undefined;
  if (dwellMask && placement) {
    nearMisses = findNearMissSpots(
      spots,
      aircraft,
      dwellMask,
      placement,
      placedAircraftPolygons,
      // shapes parameter removed
      zones
    );

    // Only include if we found any near-misses
    if (nearMisses.length === 0) {
      nearMisses = undefined;
    }
  }

  return {
    primaryBlocker,
    sizeAnalysis,
    timeAnalysis,
    durationAnalysis,
    nearMisses,
    suggestions,
  };
}

/**
 * Check gap spot status and return detailed information
 * Used for decision log entries to explain gap spot availability
 *
 * @param spot - The gap spot to check
 * @param currentPlacement - Current placement state
 * @param placedAircraftPolygons - Map of aircraft footprints (Phase 3)
 * @returns Gap spot status information
 */
function getGapSpotStatus(
  spot: Spot,
  currentPlacement: Placement,
  placedAircraftPolygons?: Map<string, Point[]>
): {
  available: boolean;
  dependenciesMet: number;
  dependenciesTotal: number;
  collision: boolean;
  reason: string;
} {
  if (!spot.isGapSpot) {
    return {
      available: true,
      dependenciesMet: 0,
      dependenciesTotal: 0,
      collision: false,
      reason: "Not a gap spot",
    };
  }

  const requiredSpots = spot.requiresOccupiedSpots || [];
  const dependenciesTotal = requiredSpots.length;

  // Count how many required spots are occupied
  const dependenciesMet = requiredSpots.filter((requiredSpotId) => {
    return Object.values(currentPlacement).some((placementData) => {
      if ("spotId" in placementData) {
        return placementData.spotId === requiredSpotId;
      }
      return false;
    });
  }).length;

  // Check if all dependencies are met
  const allDependenciesMet = dependenciesMet === dependenciesTotal;

  if (!allDependenciesMet) {
    return {
      available: false,
      dependenciesMet,
      dependenciesTotal,
      collision: false,
      reason: `Gap spot requires ${requiredSpots.join(
        ", "
      )} to be occupied (${dependenciesMet}/${dependenciesTotal} occupied)`,
    };
  }

  // Dependencies are met - check for geometry collision (Phase 3)
  // Dummy aircraft for collision check (not used in current implementation)
  const collision = hasGeometryCollision(
    spot,
    {} as ParkingRequest,
    currentPlacement,
    placedAircraftPolygons
  );

  if (collision) {
    return {
      available: false,
      dependenciesMet,
      dependenciesTotal,
      collision: true,
      reason: `Gap spot blocked by geometry collision with aircraft in required spots`,
    };
  }

  return {
    available: true,
    dependenciesMet,
    dependenciesTotal,
    collision: false,
    reason: `Gap spot available (${requiredSpots.join(
      ", "
    )} occupied, no collision)`,
  };
}

/**
 * Generate detailed blocking information for decision logging
 * Shows which spots are blocked and whether they result in traps
 *
 * @param candidateAircraft - Aircraft being considered for placement
 * @param blockedSpotIds - Spot IDs blocked by the chosen lane
 * @param placement - Current placement state
 * @param allAircraft - All aircraft in the scenario
 * @returns Array of blocking details for each blocked spot
 */
function generateBlockingDetails(
  candidateAircraft: ParkingRequest,
  blockedSpotIds: string[],
  placement: Placement,
  allAircraft: ParkingRequest[]
): Array<{
  spotId: string;
  occupiedBy?: string;
  isTrapped: boolean;
  reason: string;
}> {
  return blockedSpotIds.map((spotId) => {
    // Find if this spot is occupied
    const occupiedEntry = Object.entries(placement).find(
      ([_, placementInfo]) => {
        if ("segments" in placementInfo) {
          return placementInfo.segments[0].spotId === spotId;
        }
        return placementInfo.spotId === spotId;
      }
    );

    if (!occupiedEntry) {
      return {
        spotId,
        isTrapped: false,
        reason: "Empty (no aircraft)",
      };
    }

    const [occupiedAircraftId, _] = occupiedEntry;
    const occupiedAircraft = allAircraft.find(
      (a) => a.id === occupiedAircraftId
    );

    if (!occupiedAircraft) {
      return {
        spotId,
        isTrapped: false,
        reason: "Empty (no aircraft)",
      };
    }

    // Check if candidate would trap the occupied aircraft
    const candidateDeparture = candidateAircraft.departureBucket;
    const occupiedDeparture = occupiedAircraft.departureBucket;
    const isTrapped = candidateDeparture > occupiedDeparture;

    // Format departure times as HH:MM
    const formatBucket = (bucket: number) => {
      const hours = Math.floor(bucket / 4);
      const mins = (bucket % 4) * 15;
      return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
        2,
        "0"
      )}`;
    };

    const aircraftName = getAircraftDisplayName(occupiedAircraft);
    const reason = isTrapped
      ? `⚠️ Traps ${aircraftName} (departs ${formatBucket(
          candidateDeparture
        )} > ${formatBucket(occupiedDeparture)})`
      : `⏱️ No trap (departs ${formatBucket(
          candidateDeparture
        )} < ${formatBucket(occupiedDeparture)})`;

    return {
      spotId,
      occupiedBy: aircraftName,
      isTrapped,
      reason,
    };
  });
}

/**
 * Simple greedy first-fit placement algorithm with segmented dwell support
 * Sorts parking requests using specified strategy and assigns each to the first available spot
 * If no single spot available, tries to find a chain of spots (if plannedMoveCap > 0)
 * Updates spot occupancy as placements are made to prevent double-booking
 *
 * @param requests - List of parking requests to place
 * @param spots - List of available spots (modified in place)
 * @param bufferMinutes - Optional buffer to add before/after dwell times (default: 0)
 * @param plannedMoveCap - Maximum number of planned moves allowed (default: 0 = no segmentation)
 * @param allowTrapping - Allow placements that trap other aircraft (default: true = soft penalty mode)
 * @param egressWeight - Weight multiplier for trapped aircraft in scoring (default: 1.0, range: 1.0=low, 2.0=medium, 5.0=high)
 * @param wasteWeight - Weight multiplier for spot size waste in scoring (default: 1.0, range: 0=disabled, 0.5-3.0=increasing penalty)
 * @param structuralWeight - Weight multiplier for structural complexity penalty (default: 0.1, range: 0=disabled, 0.05-0.5=tiebreaker)
 * @param sortingStrategy - Strategy for ordering parking requests (default: 'arrival')
 * @param horizonHours - Planning horizon in hours (default: 24)
 * @param useMaskOptimization - Enable mask-based egress optimization (Phase 9)
 * @param validateMasks - Validate mask results against loop-based calculation (Phase 9, debug only)
 * @param zonePriorityWeight - Weight multiplier for zone priority penalty (default: 1.0, range: 0=disabled, 0.5-3.0=increasing preference)
 * @param zones - Array of zones with priority information (default: [])
 * @param min_dwell_weight - Weight multiplier for min dwell time penalty (default: 2.0, range: 0=disabled, 0.5-3.0=increasing preference)
 * @param stickinessWeight - Weight multiplier for stickiness penalty (default: 0.0 = disabled)
 * @param stageAircraft - Enable staging area workflow for long-stay aircraft (default: false)
 * @param stagingDwellMinutes - Time aircraft spend in staging area for servicing (default: 45 min)
 * @param quickTurnThresholdMinutes - Minimum dwell time to use staging workflow (default: 120 min = 2 hours)
 * @returns Placement map (requestId -> { spotId, orientationId } | { segments: [...] })
 */
export function parkingAlgorithm(
  requests: ParkingRequest[],
  spots: Spot[],
  bufferMinutes: number = 0,
  plannedMoveCap: number = 0,
  allowTrapping: boolean = true,
  egressWeight: number = 1.0,
  wasteWeight: number = 1.0,
  structuralWeight: number = 0.1,
  sortingStrategy: SortingStrategy = "arrival",
  horizonHours: number = 24,
  useMaskOptimization: boolean = false,
  validateMasks: boolean = false,
  zonePriorityWeight: number = 1.0,
  zones: Zone[] = [],
  min_dwell_weight: number = 2.0,
  stickinessWeight: number = 0.0,
  enableArrivalStaging: boolean = false,
  enableDepartureStaging: boolean = false,
  arrivalStagingDwellMinutes: number = 60,
  departureStagingDwellMinutes: number = 30,
  quickTurnThresholdMinutes: number = 120
): Placement {

  // Reset decision log at start
  decisionLog = [];

  const placement: Placement = {};

  // Staging Area Validation: Check if staging is configured correctly
  let effectiveEnableArrival = enableArrivalStaging;
  let effectiveEnableDeparture = enableDepartureStaging;
  let effectivePlannedMoveCap = plannedMoveCap;

  const anyStaging = enableArrivalStaging || enableDepartureStaging;

  if (anyStaging) {
    const stagingSpots = spots.filter(s => isSpotInStagingZone(s));
    if (stagingSpots.length === 0) {
      console.warn(
        `[parkingAlgorithm] WARNING: Staging enabled but no staging zones found! ` +
        `Mark zones with metadata.is_staging=true to use staging workflow. ` +
        `Disabling staging for this run to prevent placement failures.`
      );
      effectiveEnableArrival = false;  // Auto-disable both
      effectiveEnableDeparture = false;
    } else {
      console.log(
        `[parkingAlgorithm] Staging enabled: ${stagingSpots.length} staging spots available ` +
        `(arrival=${enableArrivalStaging}, departure=${enableDepartureStaging})`
      );

      // Auto-increase plannedMoveCap based on enabled staging
      // Both enabled = 2 moves, one enabled = 1 move
      const requiredMoves = (enableArrivalStaging && enableDepartureStaging) ? 2 : 1;

      if (plannedMoveCap < requiredMoves) {
        console.warn(
          `[parkingAlgorithm] WARNING: Staging enabled requires plannedMoveCap >= ${requiredMoves}. ` +
          `Auto-increasing from ${plannedMoveCap} to ${requiredMoves}.`
        );
        effectivePlannedMoveCap = requiredMoves; // Allow staging moves
      }
    }
  }

  // Phase 9: If mask optimization enabled, precompute masks once at start
  // NOTE: Masks will be updated incrementally as aircraft are placed
  if (useMaskOptimization) {
    precomputeAllEgressMasks(spots, placement, requests, 0);
  }

  // Phase 3: Initialize running polygon map for gap spot collision detection
  // TODO: Replace size-based footprints with real aircraft geometry when available
  const placedAircraftPolygons: Map<string, Point[]> = new Map();

  // Sort parking requests using specified strategy
  const sortFn = getSortingStrategy(sortingStrategy);
  const sortedRequests = sortFn(requests, spots);

  for (const request of sortedRequests) {
    const mask = dwellMask(request, bufferMinutes, horizonHours);

    // ===== LOCK PATH =====
    // Check if this aircraft has temporal locks
    const hasLocks = request.locks && request.locks.length > 0;

    if (hasLocks) {
      console.log(
        `[parkingAlgorithm] Aircraft ${request.tail_number} has ${
          request.locks!.length
        } lock(s)`
      );

      const result = findSpotChainWithLocks(
        request,
        spots,
        effectivePlannedMoveCap,
        bufferMinutes,
        horizonHours,
        egressWeight,
        wasteWeight
      );

      if (result.success) {
        // Success - place with segments
        const chain = result.chain;
        placement[request.id] = {
          segments: chain.segments,
          moveCount: chain.moveCount,
        };

        // Reserve bitmaps for each segment
        for (const segment of chain.segments) {
          const spot = spots.find((s) => s.id === segment.spotId);
          if (spot) {
            const segmentRequest: ParkingRequest = {
              ...request,
              arrivalBucket: segment.startBucket,
              departureBucket: segment.endBucket,
            };
            const segmentMask = dwellMask(segmentRequest, 0, horizonHours);
            orInto(spot.occupancy, segmentMask);
          }
        }

        // Log successful placement with lock info
        const lockedSegments = chain.segments.filter((seg) => {
          const lock = request.locks!.find(
            (l) =>
              l.spotId === seg.spotId &&
              l.startBucket === seg.startBucket &&
              l.endBucket === seg.endBucket
          );
          return !!lock;
        });

        const lockReasons = request
          .locks!.filter((l) =>
            lockedSegments.some(
              (seg) =>
                seg.spotId === l.spotId &&
                seg.startBucket === l.startBucket &&
                seg.endBucket === l.endBucket
            )
          )
          .map((l) => l.reason)
          .filter(Boolean);

        logEntry({
          aircraftId: request.id,
          type: "placed",
          spotId: chain.segments[0].spotId,
          reason: `Placed with ${chain.segments.length} segments (${lockedSegments.length} locked, ${chain.moveCount} moves)`,
          lockConstraint:
            lockedSegments.length > 0
              ? {
                  spotId: lockedSegments[0].spotId,
                  reason: lockReasons[0],
                  isLocked: true,
                  lockValidated: true,
                }
              : undefined,
        });

        // Phase 9: Recompute egress masks after locked placement
        if (useMaskOptimization) {
          precomputeAllEgressMasks(spots, placement, requests, 0);
        }

        // Phase 3: Add aircraft footprint for first segment position
        if (request.aircraft) {
          const firstSegment = chain.segments[0];
          const spot = spots.find((s) => s.id === firstSegment.spotId);
          if (spot) {
            // No null check needed - shape is required
            const footprint = createAircraftFootprint(
              request,
              spot.shape,
              firstSegment.orientationId
            );
            placedAircraftPolygons.set(request.id, footprint);
          }
        }
      } else {
        // Failed to satisfy locks - log detailed failure reason
        if (result.success === false) {
          const failure = result.failure;
          let reason: string;
          let lockSpotId: string | undefined;
          let lockReason: string | undefined;

          switch (failure.failureType) {
            case "lock_validation":
              reason = `Lock validation failed: ${failure.validationReason}`;
              lockSpotId = failure.failedLock?.spotId;
              lockReason = failure.failedLock?.reason;
              break;
            case "move_cap":
              reason = `Lock placement failed: ${failure.validationReason}`;
              // For move cap, show first lock as representative
              lockSpotId = request.locks![0].spotId;
              lockReason = request.locks![0].reason;
              break;
            case "no_spots":
              reason = `Lock placement failed: ${failure.validationReason}`;
              lockSpotId = request.locks![0].spotId;
              lockReason = request.locks![0].reason;
              break;
          }

          logEntry({
            aircraftId: request.id,
            type: "rejected",
            reason,
            lockConstraint: {
              spotId: lockSpotId || request.locks![0].spotId,
              reason: lockReason,
              isLocked: true,
              lockValidated: false,
            },
          });
        }
      }

      continue; // Skip to next aircraft (lock path handled)
    }

    // ===== STAGING PATH =====
    // Check if this aircraft should use arrival/departure staging
    const dwellMinutes = (request.departureBucket - request.arrivalBucket) * MINUTES_PER_BUCKET;

    // Window-aware staging: only stage if arrival/departure happens in planning window
    const tripArrivesInWindow = request.metadata?.tripArrivesInWindow ?? true;
    const tripDepartsInWindow = request.metadata?.tripDepartsInWindow ?? true;

    const stagingConfig = shouldUseStaging(
      request,
      effectiveEnableArrival && tripArrivesInWindow,   // Only if trip arrives in window
      effectiveEnableDeparture && tripDepartsInWindow, // Only if trip departs in window
      quickTurnThresholdMinutes,
      arrivalStagingDwellMinutes,
      departureStagingDwellMinutes
    );

    const needsStaging = stagingConfig.useArrival || stagingConfig.useDeparture;

    if (needsStaging) {
      const stagingType = stagingConfig.useArrival && stagingConfig.useDeparture
        ? "both arrival and departure"
        : stagingConfig.useArrival
        ? "arrival only"
        : "departure only";

      stagingLogger.info(
        `${request.tail_number}: Eligible for staging workflow (${dwellMinutes} min, ${stagingType})`
      );

      const arrivalStagingBuckets = Math.floor(arrivalStagingDwellMinutes / MINUTES_PER_BUCKET);
      const departureStagingBuckets = Math.floor(departureStagingDwellMinutes / MINUTES_PER_BUCKET);

      const stagingResult = findStagingChain(
        request,
        spots,
        stagingConfig.useArrival,
        stagingConfig.useDeparture,
        arrivalStagingBuckets,
        departureStagingBuckets,
        zones,
        horizonHours,
        placement,
        sortedRequests,
        egressWeight,
        wasteWeight,
        structuralWeight,
        zonePriorityWeight,
        min_dwell_weight,
        stickinessWeight,
        placedAircraftPolygons,
        logEntry
      );

      if (stagingResult.success) {
        // Success - place with staging segments
        placement[request.id] = {
          segments: stagingResult.segments,
          moveCount: stagingResult.moveCount,
        };

        // Reserve bitmaps for each segment
        for (const segment of stagingResult.segments) {
          const spot = spots.find((s) => s.id === segment.spotId);
          if (spot) {
            const segmentRequest: ParkingRequest = {
              ...request,
              arrivalBucket: segment.startBucket,
              departureBucket: segment.endBucket,
            };
            const segmentMask = dwellMask(segmentRequest, 0, horizonHours);
            orInto(spot.occupancy, segmentMask);
          }
        }

        // Build dynamic log message based on actual segments
        const segmentDescriptions = stagingResult.segments.map((seg, idx) => {
          const duration = (seg.endBucket - seg.startBucket) * MINUTES_PER_BUCKET;
          const spot = spots.find(s => s.id === seg.spotId);
          const spotName = spot?.shape?.text || seg.spotId;

          // Determine segment type based on position and configuration
          let type: string;
          if (idx === 0 && stagingConfig.useArrival) {
            type = "arrival staging";
          } else if (idx === stagingResult.segments.length - 1 && stagingConfig.useDeparture) {
            type = "departure staging";
          } else {
            type = "permanent";
          }

          return `${spotName} (${type}, ${duration} min)`;
        });

        const chainDescription = segmentDescriptions.join(" → ");

        logEntry({
          aircraftId: request.id,
          type: "placed",
          spotId: stagingResult.segments[0].spotId,
          reason: `Placed with staging workflow (${stagingResult.moveCount} moves): ${chainDescription}`,
        });

        // Phase 9: Recompute egress masks after staging placement
        if (useMaskOptimization) {
          precomputeAllEgressMasks(spots, placement, requests, 0);
        }

        // Phase 3: Add aircraft footprint for first segment position
        if (request.aircraft) {
          const firstSegment = stagingResult.segments[0];
          const spot = spots.find((s) => s.id === firstSegment.spotId);
          if (spot) {
            const footprint = createAircraftFootprint(
              request,
              spot.shape,
              firstSegment.orientationId
            );
            placedAircraftPolygons.set(request.id, footprint);
          }
        }

        continue; // Skip to next aircraft (staging path handled)
      } else {
        // Staging chain failed - log reason and fall through to regular placement
        if (stagingResult.success === false) {
          stagingLogger.warn(
            `${request.tail_number}: Staging chain failed - ${stagingResult.reason}. Falling back to regular placement.`
          );

          logEntry({
            aircraftId: request.id,
            type: "eval",
            reason: `Staging workflow failed (${stagingResult.failedSegment} segment): ${stagingResult.reason}. Attempting regular placement.`,
          });
        }

        // Fall through to normal path below
      }
    } else if (anyStaging && !needsStaging) {
      // Staging enabled but this aircraft bypasses (quick turn or insufficient time)
      stagingLogger.debug(
        `${request.tail_number}: Bypassing staging (${dwellMinutes} min ≤ ${quickTurnThresholdMinutes} min threshold or insufficient time)`
      );

      logEntry({
        aircraftId: request.id,
        type: "eval",
        reason: `Quick turn detected (${dwellMinutes} min) - bypassing staging workflow`,
      });
    }

    // ===== NORMAL PATH (no locks, no staging, or staging failed) =====
    // Log gap spot status BEFORE filtering
    // This shows gap spots that will be filtered out due to dependencies or collision
    spots.forEach((spot) => {
      if (spot.isGapSpot && spot.allowedSizes.includes(request.sizeCategory)) {
        const gapStatus = getGapSpotStatus(
          spot,
          placement,
          placedAircraftPolygons
        );

        // Only log gap spots that are NOT available (filtered out)
        if (!gapStatus.available) {
          logEntry({
            aircraftId: request.id,
            spotId: spot.id,
            type: "eval",
            timeAvailable: true, // Gap spot passed time check but failed dependency/collision
            isGapSpot: true,
            gapRequiredSpots: spot.requiresOccupiedSpots,
            gapDependenciesMet: gapStatus.dependenciesMet,
            gapDependenciesTotal: gapStatus.dependenciesTotal,
            gapCollision: gapStatus.collision,
            reason: gapStatus.reason,
          });
        }
      }
    });

    // Get all available spots with detailed breakdown tracking
    // Applies size compatibility, time availability, duration limits, and geometry collision filters (Phase 3)
    const { availableSpots: timeAvailableSpots, breakdown: filterBreakdown } =
      getAvailableSpotsWithBreakdown(
        spots,
        request,
        mask,
        placement,
        placedAircraftPolygons,
        // shapes parameter removed - spotFiltering will use spot.shape
        zones
      );

    // If no spots available after filtering, log detailed breakdown
    if (timeAvailableSpots.length === 0) {
      // Generate legacy suggestion for backward compatibility
      const suggestion = generateFilterSuggestion(filterBreakdown, request);

      // Generate enhanced suggestions with analysis (Phase 4: includes near-miss analysis)
      const suggestionDetails = generateEnhancedSuggestions(
        filterBreakdown,
        request,
        spots,
        zones,
        mask,
        placement,
        placedAircraftPolygons
        // shapes parameter removed
      );

      // Create detailed breakdown message
      const breakdownDetails = [
        `Total: ${filterBreakdown.total}`,
        filterBreakdown.disabled > 0
          ? `Disabled: ${filterBreakdown.disabled}`
          : null,
        filterBreakdown.sizeIncompatible > 0
          ? `Size incompatible: ${filterBreakdown.sizeIncompatible}`
          : null,
        filterBreakdown.timeConflict > 0
          ? `Time conflicts: ${filterBreakdown.timeConflict}`
          : null,
        filterBreakdown.durationExceeded > 0
          ? `Duration exceeded: ${filterBreakdown.durationExceeded}`
          : null,
        filterBreakdown.geometryCollision > 0
          ? `Geometry collision: ${filterBreakdown.geometryCollision}`
          : null,
      ]
        .filter(Boolean)
        .join(", ");

      logEntry({
        aircraftId: request.id,
        type: "rejected",
        reason: `No available spots (${breakdownDetails}). Suggestion: ${suggestion}`,
        filterBreakdown,
        suggestionDetails,
        sizeMismatch: filterBreakdown.sizeIncompatible > 0,
        aircraftSize: request.sizeCategory,
      });
      continue; // Skip to next parking request
    }

    if (timeAvailableSpots.length > 0) {
      // Score each spot by how many aircraft it would trap (fewer is better)
      // Apply egress weight to amplify the penalty for trapping
      const scoredSpots = timeAvailableSpots.map((spot) => {
        // Phase 9: Use fast path if mask optimization enabled
        let trappedCount: number;
        let maskDebug:
          | {
              usedMasks: boolean;
              maskResult?: number;
              loopResult?: number;
              matched?: boolean;
            }
          | undefined;

        if (useMaskOptimization) {
          const result = countTrappedAircraftFast(
            request,
            spot,
            placement,
            requests,
            spots,
            validateMasks
          );
          trappedCount = result.count;
          maskDebug = result.maskDebug;
        } else {
          trappedCount = countTrappedAircraft(
            request,
            spot,
            placement,
            requests,
            spots
          );
        }
        const trappedAircraft = findTrappedAircraft(
          request,
          spot,
          placement,
          requests,
          spots
        );

        // Multi-lane egress support: Determine which lane was chosen (best lane)
        // The algorithm uses min() logic, so we need to find the lane with fewest traps
        const egressOptions = spot.egressOptions || [];
        let chosenEgressOption:
          | { laneId: string; laneName: string; blocksCount: number }
          | undefined;
        let blockedSpots: string[] = [];

        if (egressOptions.length > 0) {
          // Find the lane with minimum traps (same logic as countTrappedAircraft)
          let minTraps = Infinity;
          let bestOption: (typeof egressOptions)[0] | undefined;

          for (const option of egressOptions) {
            // Count traps for this specific lane
            let trapsForLane = 0;
            for (const aircraftId in placement) {
              const placedInfo = placement[aircraftId];
              const placedAircraft = requests.find(
                (ac) => ac.id === aircraftId
              );
              if (!placedAircraft) continue;

              const placedSpotId =
                "segments" in placedInfo
                  ? placedInfo.segments[0].spotId
                  : placedInfo.spotId;

              if (
                option.blocksSpotIds.includes(placedSpotId) &&
                request.departureBucket > placedAircraft.departureBucket
              ) {
                trapsForLane++;
              }
            }

            if (trapsForLane < minTraps) {
              minTraps = trapsForLane;
              bestOption = option;
            }
          }

          if (bestOption) {
            chosenEgressOption = {
              laneId: bestOption.laneId,
              laneName: bestOption.laneName,
              blocksCount: bestOption.blocksSpotIds.length,
            };
            blockedSpots = bestOption.blocksSpotIds;
          }
        }

        // Calculate egress score (trapping penalty)
        // WHY: Higher weight makes algorithm strongly prefer spots that don't trap
        // EXAMPLE: trappedCount=2, egressWeight=5.0 → egressScore=10.0
        const egressScore = trappedCount * egressWeight;

        // Calculate structural complexity penalty
        // WHY: Prefer "clean" spots (no blocking) over complex ones, even when no actual conflict
        // RATIONALE: Save complex spots for when you need them, use simple spots first
        // EXAMPLE: spot blocks 3 others, structuralWeight=0.1 → structuralPenalty=0.3
        const structuralComplexity = calculateStructuralComplexity(spot);
        const structuralPenalty = structuralComplexity * structuralWeight;

        // Calculate waste score (spot size mismatch penalty)
        // WHY: Penalize using oversized spots to reserve premium capacity for large aircraft
        // SCALE: waste (0-4) × wasteWeight → comparable to egress penalty
        // EXAMPLE: Citation (small) in large spot: waste=3, wasteWeight=1.0 → wasteScore=3.0
        // STAGING EXEMPTION: Small aircraft can use large staging spots without penalty
        const wasteScore = calculateSpotWaste(request, spot);
        const isInStagingZone = isSpotInStagingZone(spot);
        const isStagingPlacement = isInStagingZone && request.isStagingSegment;
        const wastePenalty = isStagingPlacement ? 0 : wasteScore * wasteWeight;

        // Calculate dwell duration (used by multiple penalties)
        const dwellBuckets = request.departureBucket - request.arrivalBucket;
        const dwellMinutes = dwellBuckets * 15;

        // Calculate zone priority penalty
        // WHY: Prefer spots in high-priority zones (premium areas, near services)
        // FORMULA: (10 - zonePriority) × zonePriorityWeight
        // EXAMPLE: Priority 10 (best) → penalty 0, Priority 5 (neutral) → penalty 5, Priority 0 (worst) → penalty 10
        const zone = zones.find((z) => z.id === spot.zoneId);
        const zonePriority = zone?.priority ?? 5; // Default priority = 5 (neutral)
        const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

        // WARNING: Zone lookup failed - this is a data integrity issue
        if (!zone && spot.zoneId !== "default") {
          console.warn(
            `[parkingAlgorithm] ⚠️ WARNING: Spot ${spot.id} references zone "${spot.zoneId}" which was not found!`,
            `\nAvailable zones:`,
            zones.map((z) => z.id),
            `\nUsing default priority ${zonePriority}`
          );
        }

        // Calculate min dwell time penalty/bonus
        // WHY: Reserve remote/overflow spots for long-stay aircraft
        // LOGIC: Aircraft dwell >= min_dwell → BONUS (negative penalty to lower score)
        //        Aircraft dwell < min_dwell → PENALTY (positive penalty to raise score)
        // EXAMPLE: 480 min requirement, 600 min dwell → bonus=-5×weight (attracts long-stay)
        //          480 min requirement, 120 min dwell → penalty=+5×weight (repels short-stay)
        let min_dwell_penalty = 0;
        if (spot.min_dwell_minutes) {
          if (dwellMinutes >= spot.min_dwell_minutes) {
            // Long-stay aircraft → BONUS (negative penalty)
            min_dwell_penalty = -5 * min_dwell_weight;
          } else {
            // Short-stay aircraft → PENALTY (positive penalty)
            min_dwell_penalty = 5 * min_dwell_weight;
          }
        }

        // Calculate stickiness penalty (last-minute insertion)
        // WHY: Preserve existing schedule by penalizing moves from previous placements
        // LOGIC: If aircraft was in prevSpotId and we're considering a different spot, add penalty
        // VALIDATION: Only apply if prevSpotId actually exists in spot list
        // EXAMPLE: Aircraft in A1, considering A2, stickinessWeight=10 → penalty=10
        const prevSpotExists = request.prevSpotId && spots.find(s => s.id === request.prevSpotId);
        const stickyPenalty =
          prevSpotExists && request.prevSpotId !== spot.id
            ? stickinessWeight
            : 0;

        // Calculate overflow penalty for non-staging requests in staging zones
        // WHY: Discourage using staging zones for regular parking (preserve staging availability)
        // FORMULA: +5 penalty when permanent segment uses staging zone with overflow enabled
        // NOTE: This only applies when allowOverflowParking=true (otherwise spot filtered out)
        const isOverflowParking =
          !request.isStagingSegment && isSpotInStagingZone(spot);
        const overflowPenalty = isOverflowParking ? STAGING_OVERFLOW_PENALTY : 0;

        // Total score combines egress safety + structural simplicity + spot efficiency + zone preference + min dwell match + stickiness + overflow
        // Lower score = better candidate
        const totalScore =
          egressScore + structuralPenalty + wastePenalty + zonePenalty + min_dwell_penalty + stickyPenalty + overflowPenalty;

        // Log spot evaluation with both egress and waste details
        // For gap spots, include dependency and collision status
        const gapStatus = spot.isGapSpot
          ? getGapSpotStatus(spot, placement, placedAircraftPolygons)
          : null;

        // Generate detailed blocking information
        const blockingDetails =
          blockedSpots.length > 0
            ? generateBlockingDetails(
                request,
                blockedSpots,
                placement,
                requests
              )
            : undefined;

        // Check if duration exceeded (for logging)
        const duration_exceeded =
          spot.max_dwell_minutes && dwellMinutes > spot.max_dwell_minutes;

        logEntry({
          aircraftId: request.id,
          spotId: spot.id,
          type: "eval",
          timeAvailable: true,
          trappedCount,
          // Multi-lane egress fields
          chosenEgressOption,
          availableEgressOptions:
            egressOptions.length > 0 ? egressOptions.length : undefined,
          blockedSpots: blockedSpots.length > 0 ? blockedSpots : undefined,
          blockingDetails,
          blockedAircraft:
            trappedAircraft.length > 0
              ? trappedAircraft.map(getAircraftDisplayName)
              : undefined,
          egressScore,
          egressWeight,
          // Structural complexity fields
          structuralComplexity,
          structuralWeight,
          structuralPenalty,
          wasteScore,
          wasteWeight,
          wastePenalty,
          // Zone priority fields
          zonePriority,
          zonePriorityWeight,
          zonePenalty,
          // Duration fields
          max_dwell_minutes: spot.max_dwell_minutes,
          actual_dwell_minutes: dwellMinutes,
          duration_exceeded,
          // Min dwell fields
          min_dwell_minutes: spot.min_dwell_minutes,
          min_dwell_penalty,
          min_dwell_weight,
          // Stickiness fields (last-minute insertion)
          prevSpotId: request.prevSpotId,
          stickyPenalty: request.prevSpotId ? stickyPenalty : undefined, // Only log if prevSpotId set
          stickinessWeight: request.prevSpotId && stickinessWeight !== 0 ? stickinessWeight : undefined, // Only log if active
          // Overflow penalty fields (staging zone used for regular parking)
          overflowPenalty: isOverflowParking ? overflowPenalty : undefined, // Only log if overflow
          // Disabled status fields
          spot_disabled: spot.disabled,
          zone_disabled: zone?.disabled,
          // Gap spot fields
          isGapSpot: spot.isGapSpot,
          gapRequiredSpots: spot.isGapSpot
            ? spot.requiresOccupiedSpots
            : undefined,
          gapDependenciesMet: gapStatus ? gapStatus.dependenciesMet : undefined,
          gapDependenciesTotal: gapStatus
            ? gapStatus.dependenciesTotal
            : undefined,
          gapCollision: gapStatus ? gapStatus.collision : undefined,
          // Phase 9: Mask debug info (if validation mode enabled)
          maskDebug,
        });

        return { spot, trappedCount, score: totalScore };
      });

      // If allowTrapping=false, filter out spots that would trap any aircraft
      let candidateSpots = scoredSpots;
      if (!allowTrapping) {
        candidateSpots = scoredSpots.filter((s) => s.trappedCount === 0);
      }

      // If no clean spots remain (all trap) and allowTrapping=false, skip placement
      if (candidateSpots.length === 0) {
        // Find all trapped aircraft across all time-available spots
        const allTrappedAircraft = new Set<string>();
        timeAvailableSpots.forEach((spot) => {
          const trapped = findTrappedAircraft(
            request,
            spot,
            placement,
            requests,
            spots
          );
          trapped.forEach((t) =>
            allTrappedAircraft.add(getAircraftDisplayName(t))
          );
        });

        logEntry({
          aircraftId: request.id,
          type: "rejected",
          reason: `All ${timeAvailableSpots.length} time-available spots would trap aircraft (allowTrapping=false)`,
          blockedAircraft: Array.from(allTrappedAircraft),
        });
        continue; // Leave parking request unplaced
      }

      // Sort by weighted score (ascending) - pick spot with lowest egress penalty
      // WHY: Lower score = fewer traps or less impact from weight
      // EXAMPLE: score=0 (no traps) always wins, score=10 (2 traps × 5x weight) loses to score=2 (2 traps × 1x weight)
      candidateSpots.sort((a, b) => a.score - b.score);

      const bestSpot = candidateSpots[0].spot;
      const trappedAircraft = findTrappedAircraft(
        request,
        bestSpot,
        placement,
        requests,
        spots
      );

      // Full dwell in one spot - simple case
      placement[request.id] = {
        spotId: bestSpot.id,
        orientationId: bestSpot.orientations[0].id,
      };

      // Log placement success with egress details and gap spot information
      const placedGapStatus = bestSpot.isGapSpot
        ? getGapSpotStatus(bestSpot, placement, placedAircraftPolygons)
        : null;

      const gapReason =
        bestSpot.isGapSpot && bestSpot.requiresOccupiedSpots
          ? `Placed in gap spot (requires ${bestSpot.requiresOccupiedSpots.join(
              ", "
            )})`
          : undefined;

      // Find zone for placed spot
      const placedZone = zones.find((z) => z.id === bestSpot.zoneId);

      // Calculate stickiness penalty for placed spot (with validation)
      const prevSpotExists = request.prevSpotId && spots.find(s => s.id === request.prevSpotId);
      const placedStickyPenalty =
        prevSpotExists && request.prevSpotId !== bestSpot.id
          ? stickinessWeight
          : 0;

      logEntry({
        aircraftId: request.id,
        spotId: bestSpot.id,
        type: "placed",
        trappedCount: candidateSpots[0].trappedCount,
        blockedAircraft:
          trappedAircraft.length > 0
            ? trappedAircraft.map(getAircraftDisplayName)
            : undefined,
        egressScore: candidateSpots[0].score,
        egressWeight,
        // Zone priority fields
        zonePriority: placedZone?.priority,
        zonePriorityWeight,
        // Min dwell fields
        min_dwell_minutes: bestSpot.min_dwell_minutes,
        min_dwell_weight,
        // Stickiness fields
        prevSpotId: request.prevSpotId,
        stickyPenalty: request.prevSpotId ? placedStickyPenalty : undefined, // Only log if prevSpotId set
        stickinessWeight: request.prevSpotId && stickinessWeight !== 0 ? stickinessWeight : undefined, // Only log if active
        // Gap spot fields
        isGapSpot: bestSpot.isGapSpot,
        gapRequiredSpots: bestSpot.isGapSpot
          ? bestSpot.requiresOccupiedSpots
          : undefined,
        gapDependenciesMet: placedGapStatus
          ? placedGapStatus.dependenciesMet
          : undefined,
        gapDependenciesTotal: placedGapStatus
          ? placedGapStatus.dependenciesTotal
          : undefined,
        reason: gapReason,
      });

      // Reserve the spot immediately to prevent double-booking
      orInto(bestSpot.occupancy, mask);

      // Phase 9: Recompute egress masks after placement (incremental update)
      if (useMaskOptimization) {
        precomputeAllEgressMasks(spots, placement, requests, 0);
      }

      // Phase 3: Add aircraft footprint to polygon map for gap spot collision detection
      // Uses real aircraft dimensions when available (request.aircraft.wingspan/length)
      // No null check needed - shape is required
      const aircraftFootprint = createAircraftFootprint(
        request,
        bestSpot.shape,
        bestSpot.orientations[0].id
      );
      placedAircraftPolygons.set(request.id, aircraftFootprint);
    } else if (effectivePlannedMoveCap > 0) {
      // No single spot available - try segmented placement
      // Get size-compatible spots for segmentation (segmentation does its own time filtering)
      const sizeCompatibleSpots = spots.filter((spot) =>
        spot.allowedSizes.includes(request.sizeCategory)
      );
      const chain = findSpotChain(
        request,
        sizeCompatibleSpots,
        effectivePlannedMoveCap,
        bufferMinutes,
        horizonHours
      );

      if (chain) {
        // Found a valid chain within move cap
        placement[request.id] = {
          segments: chain.segments,
          moveCount: chain.moveCount,
        };

        // Log segmented placement
        logEntry({
          aircraftId: request.id,
          type: "segmented",
          reason: `${chain.segments.length} segments (${
            chain.segments.length - 1
          } moves)`,
        });

        // Reserve all segments
        applySpotChain(chain, spots);

        // Phase 9: Recompute egress masks after segmented placement
        if (useMaskOptimization) {
          precomputeAllEgressMasks(spots, placement, requests, 0);
        }

        // Phase 3: Add aircraft footprint for final segment position
        // Uses real aircraft dimensions when available (request.aircraft.wingspan/length)
        if (chain.segments.length > 0) {
          const lastSegment = chain.segments[chain.segments.length - 1];
          const spot = spots.find((s) => s.id === lastSegment.spotId);
          if (spot) {
            // No null check needed - shape is required
            const aircraftFootprint = createAircraftFootprint(
              request,
              spot.shape,
              lastSegment.orientationId
            );
            placedAircraftPolygons.set(request.id, aircraftFootprint);
          }
        }
      } else {
        // No chain found
        logEntry({
          aircraftId: request.id,
          type: "rejected",
          reason: `No single spot time-available and no valid segmentation chain found (move cap: ${effectivePlannedMoveCap})`,
        });
      }
    } else {
      // plannedMoveCap === 0 and no spot available - use breakdown from earlier
      // Note: filterBreakdown should already be defined from earlier in the loop
      const suggestion = generateFilterSuggestion(filterBreakdown, request);

      // Generate enhanced suggestions with analysis (Phase 4: includes near-miss analysis)
      const suggestionDetails = generateEnhancedSuggestions(
        filterBreakdown,
        request,
        spots,
        zones,
        mask,
        placement,
        placedAircraftPolygons
        // shapes parameter removed
      );

      const breakdownDetails = [
        `Total: ${filterBreakdown.total}`,
        filterBreakdown.disabled > 0
          ? `Disabled: ${filterBreakdown.disabled}`
          : null,
        filterBreakdown.sizeIncompatible > 0
          ? `Size incompatible: ${filterBreakdown.sizeIncompatible}`
          : null,
        filterBreakdown.timeConflict > 0
          ? `Time conflicts: ${filterBreakdown.timeConflict}`
          : null,
        filterBreakdown.durationExceeded > 0
          ? `Duration exceeded: ${filterBreakdown.durationExceeded}`
          : null,
        filterBreakdown.geometryCollision > 0
          ? `Geometry collision: ${filterBreakdown.geometryCollision}`
          : null,
      ]
        .filter(Boolean)
        .join(", ");

      logEntry({
        aircraftId: request.id,
        type: "rejected",
        reason: `No available spots (${breakdownDetails}, segmentation disabled). Suggestion: ${suggestion}`,
        filterBreakdown,
        suggestionDetails,
      });
    }
  }

  return placement;
}
